/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { theme } from 'components/styles/theme';
import { store } from 'data';
import { TemplateObjectType } from 'data/templates/api';
import { useActiveDocumentTemplateId } from 'data/templates/documents/hooks/use-active-document-id';
import { useDocumentById } from 'data/templates/documents/hooks/use-document-by-id';
import { useActiveProjectTemplate } from 'data/templates/projects';
import { useActiveStatusUpdateTemplateId } from 'data/templates/status-updates/hooks/use-active-status-update';
import { getTemplatesDetailsQueryKey, queryClient } from 'queries';
import translations from 'translations/en.json';
import { HTMLSanitizer } from 'utils/html-sanitizer';
import { FilterCriteriaAsText } from './filter-as-hb-text';

const ConditionalVariableText = ({
  variableId,
  templateType,
}: {
  variableId: any;
  templateType?: TemplateObjectType;
}) => {
  const projectTemplate = useActiveProjectTemplate();
  const activeDocTemplateId = useActiveDocumentTemplateId();
  const activeDocumentTemplate = useDocumentById(activeDocTemplateId);
  const activeStatusUpdateTemplateId = useActiveStatusUpdateTemplateId();

  const conditionalVariableData: any = useMemo(() => {
    switch (templateType) {
      case TemplateObjectType.Project:
        return projectTemplate.templateMeta?.conditionalVariables?.[variableId];

      case TemplateObjectType.Document:
        return activeDocumentTemplate?.templateMeta?.conditionalVariables?.[
          variableId
        ];

      case TemplateObjectType.StatusUpdates:
        return queryClient.getQueryData<any>(
          getTemplatesDetailsQueryKey(activeStatusUpdateTemplateId),
        )?.templateMeta?.conditionalVariables?.[variableId];

      default:
        return null;
    }
  }, [
    templateType,
    projectTemplate,
    variableId,
    activeDocumentTemplate,
    activeStatusUpdateTemplateId,
  ]);

  if (!conditionalVariableData) {
    return <></>;
  }

  const { formValues: _conditionalVariable } = conditionalVariableData;

  const conditionalVariable = _conditionalVariable ?? conditionalVariableData;

  return (
    <>
      {FieldFilter.isValid(conditionalVariable.ifBlock.condition) &&
      conditionalVariable.ifBlock.value ? (
        <>
          {'{{#if ('}
          <FilterCriteriaAsText
            criterion={conditionalVariable.ifBlock.condition}
          />
          {')}}'}
          <HTMLSanitizer text={conditionalVariable.ifBlock.value ?? ''} />
          {conditionalVariable.elseIfBlocks?.map((_block) => {
            const { condition, value } = _block;

            return (
              <>
                {FieldFilter.isValid(condition) && value ? (
                  <>
                    {'{{else if ('}
                    <FilterCriteriaAsText criterion={condition} />
                    {')}}'}
                    <HTMLSanitizer text={value ?? ''} />
                  </>
                ) : null}
              </>
            );
          })}
          {'{{else}}'}
          <HTMLSanitizer text={conditionalVariable.elseBlock ?? ''} />
          {'{{/if}}'}
        </>
      ) : null}
    </>
  );
};

export const ConditionalVariablesForBackend = ({
  variableId,
  templateType,
}: {
  variableId: any;
  templateType?: TemplateObjectType;
}) => {
  return (
    <Provider store={store}>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <IntlProvider locale="en" messages={translations}>
            <QueryClientProvider client={queryClient}>
              <ConditionalVariableText
                variableId={variableId}
                templateType={templateType}
              />
            </QueryClientProvider>
          </IntlProvider>
        </ThemeProvider>
      </BrowserRouter>
    </Provider>
  );
};
